import { computed, effect, inject } from '@angular/core';
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { ApplicationStore } from '../application/application.store';
import { withStoreDevtools } from '../with-devtools';

export interface BreadcrumbNavigationPath {
  label: string;
  path?: string;
  avatar?: {
    label: string;
    srcImage: string | undefined;
  };
}

interface BreadcrumbState {
  paths: BreadcrumbNavigationPath[];
  hidden: boolean;
}

const initialState: BreadcrumbState = {
  paths: [],
  hidden: false,
};

export const BreadcrumbStore = signalStore(
  { providedIn: 'root' },
  withStoreDevtools('breadcrumb'),
  withState(initialState),
  withComputed((store) => ({
    navigationPaths: computed(() => (store.hidden() ? [] : store.paths())),
    isHidden: computed(() => store.hidden()),
  })),
  withMethods((store) => ({
    setPaths(paths: BreadcrumbNavigationPath[]) {
      patchState(store, { paths });
    },
    setHidden(hidden: boolean) {
      patchState(store, { hidden });
    },
    appendPath(path: BreadcrumbNavigationPath) {
      patchState(store, { paths: [...store.paths(), path] });
    },
    updatePath(oldLabel: string, newBreadcrumb: BreadcrumbNavigationPath) {
      const currentPaths = store.paths(); // get the array from signal
      const index = currentPaths.findIndex(
        (b) => b.label === oldLabel || (newBreadcrumb.path && b.path === newBreadcrumb.path),
      );
      if (index !== -1) {
        const updated = [...currentPaths];
        updated[index] = newBreadcrumb;
        patchState(store, { paths: updated }); // <- patchState updates the store properly
      }
    },
    clear() {
      patchState(store, { paths: [], hidden: false });
    },
  })),
  withHooks((store) => {
    const applicationStore = inject(ApplicationStore);

    return {
      onInit() {
        effect(() => {
          if (applicationStore.debug()) {
            console.debug('Breadcrumb state:', getState(store));
          }
        });
      },
    };
  }),
);
